export type AnnexKind = 'subscriptions' | 'consumptions';

export interface AnnexValidation {
  ok: boolean;
  error?: string;
}

/**
 * Client-side guard: read only the header row of the uploaded xlsx (sheetjs is
 * lazy-loaded, `sheetRows: 1` keeps it fast even on the ~10 Mo consommations
 * file) and check it looks like the expected raw Sewan annex — not a generated
 * "Détail" file, and not the two annexes swapped. Mirrors the backend guard.
 */
export async function validateAnnexHeader(
  file: File,
  kind: AnnexKind
): Promise<AnnexValidation> {
  try {
    const XLSX = await import('xlsx');
    const data = new Uint8Array(await file.arrayBuffer());
    const workbook = XLSX.read(data, { type: 'array', sheetRows: 1 });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const header = (XLSX.utils.sheet_to_json(sheet, { header: 1 })[0] ??
      []) as unknown[];

    const raw = (i: number): string =>
      typeof header[i] === 'string' ? header[i].trim() : '';
    const cell = (i: number): string => raw(i).toLowerCase();
    const seen = (i: number): string => raw(i) || '(vide)';

    if (!cell(0).includes('valorisation')) {
      return {
        ok: false,
        error: `Ce fichier ne ressemble pas à un export Sewan : la 1ʳᵉ colonne est « ${seen(0)} » au lieu de « Id interne de valorisation ». C’est probablement un fichier « Détail » généré.`,
      };
    }
    if (kind === 'subscriptions' && !cell(6).startsWith('param')) {
      return {
        ok: false,
        error: `Ce fichier ne ressemble pas à l’annexe abonnements : la colonne G est « ${seen(6)} » au lieu de « Param1 ». Les deux annexes sont peut-être inversées.`,
      };
    }
    if (kind === 'consumptions' && !cell(6).includes('appelant')) {
      return {
        ok: false,
        error: `Ce fichier ne ressemble pas à l’annexe consommations : la colonne G est « ${seen(6)} » au lieu de « Numéro appelant ». Les deux annexes sont peut-être inversées.`,
      };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'Fichier illisible (un .xlsx est attendu).' };
  }
}
